import { Children, isValidElement, ReactNode } from 'react';
import { Header, HeaderContext } from '@tanstack/react-table';
import { AdditionalHeaderDefinition, AdditionalHeaderRow, CustomColumnDef, StickyPosition } from '@/types/table';
import { DEFAULT_MAX_COLUMN_SIZE, DEFAULT_TABLE_WIDTH } from './constants';
import { convertRemToTableSizeUnits, formatTableSizeUnitsAsRem } from './columnLayout.constants';

const TABLE_HEADER_HORIZONTAL_PADDING_REM = 3.2;
const TABLE_HEADER_CONTENT_GAP_REM = 0.8;
const TABLE_SORTING_INDICATOR_WIDTH_REM = 2.4;
const TABLE_GROUPING_INDICATOR_WIDTH_REM = 2;
const TABLE_HEADER_CHARACTER_WIDTH_REM = 0.45;

export interface MergedGroupInfo {
  groupLabel: string;
  isFirst: boolean;
  isLast: boolean;
  colSpan: number;
}

export type PositionedColumnDef<T> = CustomColumnDef<T> & {
  headerDisplayName: string;
  sortable: boolean;
};

type HeaderCellWidthOptions = {
  currentWidth?: number;
  headerLabel: string;
  isSortable: boolean;
  isGroupable: boolean;
};

export const isStickyLeft = (sticky?: boolean | StickyPosition): boolean => sticky === true || sticky === 'left';

export const isStickyRight = (sticky?: boolean | StickyPosition): boolean => sticky === 'right';

export const isSticky = (sticky?: boolean | StickyPosition): boolean => isStickyLeft(sticky) || isStickyRight(sticky);

const getHeaderLabelLength = (headerLabel: string): number => {
  return Math.max(headerLabel.trim().length, 1);
};

const isAdditionalHeaderCell = (value: unknown): value is AdditionalHeaderDefinition => {
  return value !== null && typeof value === 'object' && 'label' in value && 'enableSorting' in value;
};

const getIsSortableAdditionalHeader = <T>(
  additionalHeaderRow: AdditionalHeaderRow<T> | undefined,
  columnId: string,
): boolean => {
  return isAdditionalHeaderCell(additionalHeaderRow?.data?.[columnId]);
};

type HeaderRenderTemplate<T> = (context: HeaderContext<T, unknown>) => ReactNode;

const isHeaderRenderTemplate = <T>(value: unknown): value is HeaderRenderTemplate<T> => typeof value === 'function';

const toRenderableNode = (value: unknown): ReactNode => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'bigint') return value.toString();
  if (Array.isArray(value)) return value.map(toRenderableNode);
  if (isValidElement<{ children?: ReactNode }>(value)) return value;
  return null;
};

const getRenderableTextContent = (content: ReactNode): string => {
  if (typeof content === 'string' || typeof content === 'number') return String(content).trim();
  if (Array.isArray(content)) return content.map(getRenderableTextContent).filter(Boolean).join(' ').trim();
  if (isValidElement<{ children?: ReactNode }>(content))
    return getRenderableTextContent(Children.toArray(content.props.children));
  return '';
};

const getHeaderDisplayName = <T>(header: Header<T, unknown>, mergedGroupLabel?: string): string => {
  if (mergedGroupLabel) return mergedGroupLabel;
  if (typeof header.column.columnDef.header === 'string' || typeof header.column.columnDef.header === 'number') {
    return String(header.column.columnDef.header);
  }
  return header.column.columnDef.id && typeof header.column.columnDef.id === 'string'
    ? header.column.columnDef.id
    : header.column.id;
};

const getHeaderSizingLabel = <T>(
  header: Header<T, unknown>,
  mergedGroupLabel: string | undefined,
  headerDisplayName: string,
): string => {
  if (mergedGroupLabel) return mergedGroupLabel;
  const headerTemplate: unknown = header.column.columnDef.header;
  const renderedHeader = isHeaderRenderTemplate<T>(headerTemplate)
    ? headerTemplate(header.getContext())
    : toRenderableNode(headerTemplate);
  return getRenderableTextContent(renderedHeader) || headerDisplayName;
};

const getMinimumHeaderWidth = ({
  headerLabel,
  isSortable,
  isGroupable,
}: Omit<HeaderCellWidthOptions, 'currentWidth'>): number => {
  const headerAllowanceRem =
    TABLE_HEADER_HORIZONTAL_PADDING_REM +
    (isSortable ? TABLE_SORTING_INDICATOR_WIDTH_REM + TABLE_HEADER_CONTENT_GAP_REM : 0) +
    (isGroupable ? TABLE_GROUPING_INDICATOR_WIDTH_REM + TABLE_HEADER_CONTENT_GAP_REM : 0);
  return convertRemToTableSizeUnits(
    getHeaderLabelLength(headerLabel) * TABLE_HEADER_CHARACTER_WIDTH_REM + headerAllowanceRem,
  );
};

export const getMergedGroupInfo = (
  columns: { mergedGroup?: string }[],
  columnIndex: number,
): MergedGroupInfo | null => {
  const column = columns[columnIndex];
  if (!column?.mergedGroup) return null;
  const groupLabel = column.mergedGroup;
  let groupStart = columnIndex;
  while (groupStart > 0 && columns[groupStart - 1]?.mergedGroup === groupLabel) {
    groupStart--;
  }
  let groupEnd = columnIndex;
  while (groupEnd < columns.length - 1 && columns[groupEnd + 1]?.mergedGroup === groupLabel) {
    groupEnd++;
  }
  return {
    groupLabel,
    isFirst: columnIndex === groupStart,
    isLast: columnIndex === groupEnd,
    colSpan: groupEnd - groupStart + 1,
  };
};

export const getMergedGroupWidth = (
  columns: { mergedGroup?: string; size?: number }[],
  columnIndex: number,
): number | null => {
  const mergedGroup = getMergedGroupInfo(columns, columnIndex);
  if (!mergedGroup) return null;
  let groupStart = columnIndex;
  while (groupStart > 0 && columns[groupStart - 1]?.mergedGroup === mergedGroup.groupLabel) {
    groupStart--;
  }
  return columns
    .slice(groupStart, groupStart + mergedGroup.colSpan)
    .reduce((totalWidth, column) => totalWidth + (column.size ?? 0), 0);
};

export const getHeaderCellWidth = ({
  currentWidth,
  headerLabel,
  isSortable,
  isGroupable,
}: HeaderCellWidthOptions): number | undefined => {
  const hasExplicitWidth = typeof currentWidth === 'number' && currentWidth > 0;
  if (!isSortable) return hasExplicitWidth ? currentWidth : undefined;
  const minimumHeaderWidth = getMinimumHeaderWidth({ headerLabel, isSortable, isGroupable });
  if (!hasExplicitWidth) return minimumHeaderWidth;
  return Math.max(currentWidth, minimumHeaderWidth);
};

export const getColumnWidthStyle = (size: number | undefined): string | undefined => {
  if (typeof size !== 'number' || size <= 0) return undefined;
  return formatTableSizeUnitsAsRem(size);
};

export const getPositionedColumnDefs = <T>(
  headers: Header<T, unknown>[],
  additionalHeaderRow?: AdditionalHeaderRow<T>,
): PositionedColumnDef<T>[] => {
  const visibleColumnDefs: CustomColumnDef<T>[] = headers.map(header => header.column.columnDef);
  return headers.map((header, headerIndex) => {
    const columnDef: CustomColumnDef<T> = header.column.columnDef;
    const mergedGroup = getMergedGroupInfo(visibleColumnDefs, headerIndex);
    const headerDisplayName = getHeaderDisplayName(header, mergedGroup?.groupLabel);
    const headerSizingLabel = getHeaderSizingLabel(header, mergedGroup?.groupLabel, headerDisplayName);
    const sortable =
      !mergedGroup &&
      header.column.getCanSort() &&
      !getIsSortableAdditionalHeader(additionalHeaderRow, header.column.id);

    return {
      ...columnDef,
      headerDisplayName,
      sortable,
      size:
        getHeaderCellWidth({
          currentWidth: header.getSize(),
          headerLabel: headerSizingLabel,
          isSortable: sortable,
          isGroupable: header.column.getCanGroup(),
        }) ?? header.getSize(),
    };
  });
};

const sumColumnSizes = <T>(columns: CustomColumnDef<T>[]): number =>
  columns.reduce((sum, col) => sum + (col.size ?? 0), 0);

const hasExplicitSize = <T>(col: CustomColumnDef<T>): boolean => (col.size ?? -1) > 0;

const isBelowMaxSize = <T>(col: CustomColumnDef<T>): boolean => (col.size ?? 0) < DEFAULT_MAX_COLUMN_SIZE;

export const distributeColumnSizes = <T>(
  cols: CustomColumnDef<T>[],
  minColumnSize: number,
  targetWidth = DEFAULT_TABLE_WIDTH,
): void => {
  const visibleCols = cols.filter(col => col.visible !== false);
  const withSize = visibleCols.filter(hasExplicitSize);
  const withoutSize = visibleCols.filter(col => !hasExplicitSize(col));

  if (withoutSize.length > 0 && withSize.length > 0) {
    const usedWidth = sumColumnSizes(withSize);
    const remaining = Math.max(targetWidth - usedWidth, withoutSize.length * minColumnSize);
    const perColumn = Math.floor(remaining / withoutSize.length);
    withoutSize.forEach(col => {
      col.size = perColumn;
    });
  } else if (withoutSize.length === visibleCols.length) {
    const perColumn = Math.floor(targetWidth / visibleCols.length);
    withoutSize.forEach(col => {
      col.size = perColumn;
    });
  } else {
    const totalWidth = sumColumnSizes(visibleCols);
    if (totalWidth < targetWidth && totalWidth > 0) {
      let extraSpace = targetWidth - totalWidth;
      let candidates = visibleCols.filter(isBelowMaxSize);

      while (extraSpace > 0 && candidates.length > 0) {
        const candidateTotal = sumColumnSizes(candidates);
        const spaceToDistribute = extraSpace;
        let overflow = 0;
        const nextCandidates: CustomColumnDef<T>[] = [];

        candidates.forEach(col => {
          const currentSize = col.size ?? 0;
          const extra = Math.floor((currentSize / candidateTotal) * spaceToDistribute);
          const newSize = currentSize + extra;

          if (newSize >= DEFAULT_MAX_COLUMN_SIZE) {
            overflow += newSize - DEFAULT_MAX_COLUMN_SIZE;
            col.size = DEFAULT_MAX_COLUMN_SIZE;
          } else {
            col.size = newSize;
            nextCandidates.push(col);
          }
        });

        extraSpace = overflow;
        candidates = nextCandidates;
      }

      const gap = targetWidth - sumColumnSizes(visibleCols);
      if (gap > 0 && visibleCols.length > 0) {
        const lastResizable = [...visibleCols].reverse().find(isBelowMaxSize);
        if (lastResizable) {
          lastResizable.size = (lastResizable.size ?? 0) + gap;
        }
      }
    }
  }
};

export { buildLeftOffsets, buildRightOffsets, buildColumnOffsets, getStickyColumnProps } from './columnLayout.utils';
export type { ColumnOffsets, StickyColumnProps } from './columnLayout.utils';
